import ReactGA from 'react-ga4';

type GoogleAnalyticsEventParameters = Record<string, string | number | boolean | null | undefined>;

let initializedMeasurementId: string | undefined;
let lastTrackedPagePath: string | undefined;

function getTrimmedEnvValue(value: string | undefined): string | undefined {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : undefined;
}

function isDevelopmentEnvironment(): boolean {
  const viteDevelopmentFlag = getTrimmedEnvValue(import.meta.env.VITE_DEVELOPMENT);
  return viteDevelopmentFlag ? viteDevelopmentFlag.toLowerCase() === 'true' : import.meta.env.DEV;
}

function getGoogleAnalyticsMeasurementId(): string | undefined {
  return getTrimmedEnvValue(import.meta.env.VITE_GA_MEASUREMENT_ID);
}

function getAnalyticsEnabledOverride(): boolean | undefined {
  const analyticsEnabled = getTrimmedEnvValue(import.meta.env.VITE_GA_ENABLED);
  if (!analyticsEnabled) {
    return undefined;
  }

  return analyticsEnabled.toLowerCase() === 'true';
}

export function isGoogleAnalyticsEnabled(): boolean {
  if (!getGoogleAnalyticsMeasurementId()) {
    return false;
  }

  const enabledOverride = getAnalyticsEnabledOverride();
  if (enabledOverride !== undefined) {
    return enabledOverride;
  }

  return !isDevelopmentEnvironment();
}

export function initializeGoogleAnalytics(): void {
  const measurementId = getGoogleAnalyticsMeasurementId();

  if (!measurementId || !isGoogleAnalyticsEnabled()) {
    return;
  }

  if (initializedMeasurementId === measurementId) {
    return;
  }

  // Disable GA4's automatic page_view so route changes are tracked manually.
  ReactGA.initialize(measurementId, {
    gtagOptions: {
      send_page_view: false,
    },
    testMode: import.meta.env.MODE === 'test',
  });

  initializedMeasurementId = measurementId;
}

function isGoogleAnalyticsReady(): boolean {
  initializeGoogleAnalytics();
  return initializedMeasurementId !== undefined;
}

export function trackPageView(path: string): void {
  if (!isGoogleAnalyticsReady() || lastTrackedPagePath === path) {
    return;
  }

  lastTrackedPagePath = path;

  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: document.title,
  });
}

export function trackGoogleAnalyticsEvent(eventName: string, parameters: GoogleAnalyticsEventParameters = {}): void {
  if (!isGoogleAnalyticsReady()) {
    return;
  }

  ReactGA.event(eventName, parameters);
}
